import { Box, Typography } from '@material-ui/core'
import * as Sentry from '@sentry/react'
import { jwtDecode } from 'jwt-decode'
import { ChangeEvent, FormEvent, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import Button from '@/components/button'
import Card from '@/components/card'
import InputText from '@/components/input-text'
import { useTimeLeft } from '@/hooks/use-time-left'
import { routes } from '@/routes/config'
import { recoveryUserPassword } from '@/services/recovery-user-password'

import { useStyles } from './styles'

type TokenPayload = {
  data: {
    id: number
    name: string
    email: string
  }
  exp: number
}

export default function PasswordReset() {
  const classes = useStyles()
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { timeLeft, setTimeLeft } = useTimeLeft(() => {
    navigate(routes.Login())
  })
  const [userData, setUserData] = useState<TokenPayload['data']>()
  const [newPassword, setNewPassword] = useState({
    value: '',
    isValid: false,
    message: '',
  })
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState({
    value: '',
    isValid: false,
    message: '',
  })

  const [formSendingStatus, setFormSendingStatus] = useState<
    'waiting' | 'sent' | 'sending'
  >('waiting')

  function verifyToken() {
    try {
      const payloadToken: TokenPayload = jwtDecode(token!)

      if (!payloadToken?.data)
        return setNewPassword({
          value: '',
          isValid: false,
          message: 'Token inválido',
        })

      const expDate = new Date(payloadToken.exp * 1000)
      const actualDate = new Date()

      if (expDate < actualDate) {
        const formattedTokenDate = expDate
          .toLocaleString([], {
            dateStyle: 'short',
            timeStyle: 'short',
          })
          .replace(' ', ' às ')

        return setNewPassword({
          value: '',
          isValid: false,
          message: `O token expirou em ${formattedTokenDate}, solicite um novo!`,
        })
      }

      setUserData(payloadToken.data)
    } catch (error) {
      Sentry.captureException(error)
      setNewPassword({
        value: '',
        isValid: false,
        message: 'Token inválido',
      })
    }
  }

  async function formSubmitHandler(event: FormEvent) {
    event.preventDefault()

    if (!userData) {
      setNewPassword({
        value: '',
        isValid: false,
        message: 'Token inválido',
      })
      setNewPasswordConfirmation({
        value: '',
        isValid: false,
        message: '',
      })
      return
    }

    setFormSendingStatus('sending')
    try {
      await recoveryUserPassword(userData.id, newPassword.value)
      setFormSendingStatus('sent')
      setTimeLeft(5)
    } catch (e) {
      setNewPassword({
        value: '',
        isValid: false,
        message: 'Ocorreu um erro ao enviar a nova senha, tente novamente!',
      })
      setNewPasswordConfirmation({
        value: '',
        isValid: false,
        message: '',
      })
      setFormSendingStatus('waiting')
    }
  }

  function newPasswordInputHandler(e: ChangeEvent<HTMLInputElement>) {
    const value = e.target.value.trim()

    if (value) {
      if (value.length > 20) {
        setNewPassword({
          value,
          isValid: false,
          message: 'É preciso ter no máximo 20 caracteres',
        })
      } else if (value.length < 8) {
        setNewPassword({
          value,
          isValid: false,
          message: 'É preciso ter no mínimo 8 caracteres',
        })
      } else {
        setNewPassword({ value, isValid: true, message: '' })
      }
    } else {
      setNewPassword({ value, isValid: false, message: '' })
    }
  }

  function newPasswordConfirmationInputHandler(
    e: ChangeEvent<HTMLInputElement>,
  ) {
    const value = e.target.value.trim()
    setNewPasswordConfirmation((state) => ({ ...state, value }))
  }

  useEffect(() => {
    if (
      newPassword.value !== newPasswordConfirmation.value &&
      newPasswordConfirmation.value
    ) {
      setNewPasswordConfirmation((state) => ({
        ...state,
        isValid: false,
        message: 'Senhas não coincidem!',
      }))
    } else if (newPasswordConfirmation.value) {
      setNewPasswordConfirmation((state) => ({
        ...state,
        isValid: true,
        message: '',
      }))
    } else {
      setNewPasswordConfirmation((state) => ({
        ...state,
        isValid: false,
        message: '',
      }))
    }
  }, [newPassword.value, newPasswordConfirmation.value])

  useEffect(() => {
    verifyToken()
  }, [])

  return (
    <Box className={classes.main}>
      <Card className={classes.card}>
        <Typography variant='h4'>Redefinir senha</Typography>
        {formSendingStatus !== 'sent' ? (
          <>
            <Typography variant={'subtitle1'}>
              Insira sua nova senha com no mínimo 8 caracteres
            </Typography>
            <form onSubmit={formSubmitHandler} className={classes.form}>
              <InputText
                id='new-password'
                value={newPassword.value}
                onChange={newPasswordInputHandler}
                className={classes.input}
                label={'Nova senha'}
                type={'password'}
                success={newPassword.isValid}
                error={!newPassword.isValid && !!newPassword.message}
                helperText={newPassword.message}
              />
              <InputText
                id='new-password-confirmation'
                value={newPasswordConfirmation.value}
                onChange={newPasswordConfirmationInputHandler}
                className={classes.input}
                label={'Confirme a nova senha'}
                type={'password'}
                success={newPasswordConfirmation.isValid}
                error={
                  !newPasswordConfirmation.isValid &&
                  !!newPasswordConfirmation.message
                }
                helperText={newPasswordConfirmation.message}
              />
              <Button
                disabled={
                  !newPassword.isValid || !newPasswordConfirmation.isValid
                }
                loading={formSendingStatus === 'sending'}
                className={classes.submitButton}
                type={'submit'}
                outlined
              >
                Redefinir senha
              </Button>
            </form>
          </>
        ) : (
          <div className={classes.formSent}>
            <Typography>Sua senha foi alterada.</Typography>
            <Typography>
              Você será redirecionado para a página de login em {timeLeft}{' '}
              segundos.
            </Typography>
          </div>
        )}
      </Card>
    </Box>
  )
}
